import type { TFunction } from 'i18next';
import type {
  NeedsAttentionAction,
  NeedsAttentionFeed,
  NeedsAttentionItem,
} from '@gokrew/models/company-dashboard';
import { formatDate } from './utility';

type DisplayValue = string | number | boolean | null;

/**
 * Display values derived from a "Needs attention" item's raw params, by name,
 * so every item type in either portal writes dates, day counts, missing
 * fields and bilingual labels the same way. Keys read from the portal's own
 * namespace: `needs_attention.days`, `needs_attention.waiting_on_*`,
 * `needs_attention.field.*` and `needs_attention.more_fields`.
 */
export function needsAttentionDisplayParams(
  params: NeedsAttentionItem['params'],
  t: TFunction,
  locale: string
): Record<string, DisplayValue> {
  // Objects (the leave lines' team overlap) render as their own line, never
  // inside the sentence.
  const out: Record<string, DisplayValue> = {};
  for (const [name, value] of Object.entries(params)) {
    if (value === null || typeof value !== 'object') out[name] = value;
  }
  const date = (value: unknown) =>
    formatDate(String(value), { format: 'D MMM', locale });
  const french = locale.toLowerCase().startsWith('fr');
  if (params.startDate) {
    out.dates =
      !params.endDate || params.endDate === params.startDate
        ? date(params.startDate)
        : `${date(params.startDate)} - ${date(params.endDate)}`;
  }
  if (params.date) out.date = date(params.date);
  // "Why?" lines (smart signals): dates that may be years back show the year.
  const fullDate = (value: unknown) =>
    formatDate(String(value), { format: 'D MMM YYYY', locale });
  for (const key of ['since', 'joined', 'due'] as const) {
    if (params[key]) out[key] = fullDate(params[key]);
  }
  if (typeof params.terminationReason === 'string') {
    out.terminationReason = t(
      `needs_attention.signals.termination_reason.${params.terminationReason}`
    );
  }
  if (typeof params.salaryReason === 'string' && params.salaryReason) {
    out.salaryReason = t(
      `needs_attention.signals.salary_reason.${params.salaryReason}`
    );
  }
  const days = (value: unknown) =>
    t('needs_attention.days', { count: Number(value) });
  if (params.available != null) out.availableDays = days(params.available);
  if (params.after != null) out.afterDays = days(params.after);
  if (params.pendingDays != null)
    out.pendingDaysLabel = days(params.pendingDays);
  if (params.waitingOnKind) {
    out.waitingOnLabel =
      params.waitingOnKind === 'hr'
        ? t('needs_attention.waiting_on_hr')
        : params.waitingOnKind === 'none'
          ? t('needs_attention.waiting_on_nobody')
          : (out.waitingOn ?? t('needs_attention.waiting_on_approvers'));
  }
  if (typeof params.fields === 'string' && params.fields) {
    const labels = params.fields
      .split(',')
      .map((field) => t(`needs_attention.field.${field}`));
    const more = Number(params.moreFields ?? 0);
    out.fields =
      labels.join(', ') +
      (more > 0 ? ` ${t('needs_attention.more_fields', { count: more })}` : '');
  }
  // Survey trend lines (spec 1.2): native units with one decimal (4,1 in
  // French), the Index in whole points.
  if (params.trendUnit) {
    const native = params.trendUnit === 'native';
    const number = (value: unknown) =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: native ? 1 : 0,
        maximumFractionDigits: native ? 1 : 0,
      }).format(Number(value));
    if (params.fromValue != null) out.from = number(params.fromValue);
    if (params.toValue != null) out.to = number(params.toValue);
    if (params.deltaValue != null) {
      out.delta = native
        ? number(params.deltaValue)
        : t('needs_attention.survey_points', {
            count: Number(params.deltaValue),
          });
    }
  }
  if (french && out.jobFr) out.job = out.jobFr;
  if (french && out.stageFr) out.stage = out.stageFr;
  return out;
}

/** An item's buttons; a cached feed from before `actions` has only `action`. */
export const needsAttentionActions = (
  item: NeedsAttentionItem
): NeedsAttentionAction[] =>
  item.actions?.length ? item.actions : [item.action];

/** A signal line (smart signals, D134): a prompt, never counted in badges. */
export const isSignalItem = (item: NeedsAttentionItem): boolean =>
  item.group === 'signal';

/** Rows of a short list kept for signals when actions fill it (D134). */
export const SIGNAL_RESERVED_ROWS = 2;

/**
 * The compact list cut to the `rows` that fit, the server's way (D134):
 * actions first; when they would fill every row, the last `min(2, signals)`
 * rows (never more than half) are kept for signals, else signals fill the
 * rest. `maxSignalRows` caps the signal rows (My team: 2).
 */
export function fitNeedsAttentionRows(
  items: NeedsAttentionItem[],
  rows: number,
  maxSignalRows = Number.MAX_SAFE_INTEGER
): { actions: NeedsAttentionItem[]; signals: NeedsAttentionItem[] } {
  const actions = items.filter((item) => !isSignalItem(item));
  const signals = items.filter(isSignalItem);
  const reserved =
    actions.length >= rows
      ? Math.min(SIGNAL_RESERVED_ROWS, signals.length, Math.floor(rows / 2))
      : 0;
  const shownActions = actions.slice(0, Math.max(0, rows - reserved));
  return {
    actions: shownActions,
    signals: signals.slice(
      0,
      Math.min(Math.max(0, rows - shownActions.length), maxSignalRows)
    ),
  };
}

const ROLLUP_PREFIX = 'rollup:';

/**
 * The feed as it reads right after hiding `itemId` (a line, or a rollup row
 * and every line it stands for), so the row and the badges drop at once
 * while the server's fresh feed loads. The refetch replaces it.
 */
export function hideNeedsAttentionLocally(
  feed: NeedsAttentionFeed,
  itemId: string
): NeedsAttentionFeed {
  const rollupType = itemId.startsWith(ROLLUP_PREFIX)
    ? itemId.slice(ROLLUP_PREFIX.length)
    : null;
  const gone = (item: NeedsAttentionItem) =>
    item.id === itemId || (!!rollupType && item.type === rollupType);
  const all = feed.all.filter((item) => !gone(item));
  const signals = (feed.signals ?? []).filter((item) => !gone(item));
  const counts: Record<string, number> = {};
  for (const item of all) counts[item.type] = (counts[item.type] ?? 0) + 1;
  if (feed.signals) counts.signals = signals.length;
  return {
    ...feed,
    items: feed.items.filter((item) => !gone(item)),
    all,
    total: all.length,
    counts,
    forViewer: all.filter((item) => item.forViewer).length,
    hidden: [
      ...(feed.hidden ?? []),
      ...feed.all.filter(gone),
      ...(feed.signals ?? []).filter(gone),
    ],
    ...(feed.signals ? { signals } : {}),
  };
}

/**
 * A "Prepare the meeting" invitation with the employee's first name in place
 * of `{{firstName}}` (the server never sends a name to the model).
 */
export const fillPrepareInvitation = (
  invitation: string,
  firstName: string | null | undefined
): string =>
  invitation.replace(/\{\{firstName\}\}/g, firstName?.trim() || '').trim();
